
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useState } from "react";
import { Upload } from "lucide-react";

const reportFormSchema = z.object({
  title: z.string().min(1, "Title is required"),
  link: z.string().min(1, "Link is required").url("Must be a valid URL"),
  table: z.enum(['reports', 'report_submissions'], {
    required_error: "Please select where to store the report",
  }),
});

type ReportFormValues = z.infer<typeof reportFormSchema>;

interface ReportFormProps {
  onSubmit: (data: ReportFormValues & { imageFile?: File }) => void;
}

export const ReportForm = ({ onSubmit }: ReportFormProps) => {
  const [imageFile, setImageFile] = useState<File | null>(null);
  
  const form = useForm<ReportFormValues>({
    resolver: zodResolver(reportFormSchema),
    defaultValues: {
      title: "",
      link: "",
      table: "reports",
    },
  });

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setImageFile(e.target.files[0]);
    }
  };

  const handleSubmit = (data: ReportFormValues) => {
    onSubmit({
      ...data,
      imageFile: imageFile || undefined
    });
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Title</FormLabel>
              <FormControl>
                <Input placeholder="Enter report title" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="link"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Link</FormLabel>
              <FormControl>
                <Input 
                  placeholder="Enter report link"
                  type="url"
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="space-y-2">
          <FormLabel>Report Image (Optional)</FormLabel>
          <div className="flex items-center gap-2">
            <Input
              id="reportImage"
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="flex-1"
            />
            <Button 
              type="button" 
              variant="outline" 
              size="icon" 
              onClick={() => document.getElementById('reportImage')?.click()}
            >
              <Upload className="h-4 w-4" />
            </Button>
          </div>
          {imageFile && (
            <p className="text-sm text-muted-foreground">
              Selected image: {imageFile.name}
            </p>
          )}
        </div>

        <FormField
          control={form.control}
          name="table"
          render={({ field }) => (
            <FormItem className="space-y-3">
              <FormLabel>Store In</FormLabel>
              <FormControl>
                <RadioGroup
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  className="flex flex-col space-y-1"
                >
                  <FormItem className="flex items-center space-x-3 space-y-0">
                    <FormControl>
                      <RadioGroupItem value="reports" />
                    </FormControl>
                    <FormLabel className="font-normal">
                      Reports
                    </FormLabel>
                  </FormItem>
                  <FormItem className="flex items-center space-x-3 space-y-0">
                    <FormControl>
                      <RadioGroupItem value="report_submissions" />
                    </FormControl>
                    <FormLabel className="font-normal">
                      Submissions
                    </FormLabel>
                  </FormItem>
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type="submit" className="w-full">
          Create Report
        </Button>
      </form>
    </Form>
  );
};
